/**
 * Goovi Analytics — GTM / dataLayer integration
 * Mirrors the Hydrogen DataLayerIntegration.tsx logic for the Liquid storefront.
 *
 * Depends on window.goovi being populated by theme.liquid before this script runs:
 *   window.goovi.pageData    – page type, cart totals, locale
 *   window.goovi.customer    – raw customer fields (hashed here client-side)
 *   window.goovi.productData – set by jkl-pdp-main.liquid on product pages
 *   window.goovi.collectionProducts – set by jkl-collection-product-grid.liquid
 *   window.goovi.cartData    – set by jkl-main-cart.liquid on cart page
 *
 * Public API: window.gooviAnalytics.*
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Utility helpers
  // ---------------------------------------------------------------------------

  async function sha256(str) {
    const data = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(function (b) { return b.toString(16).padStart(2, '0'); })
      .join('');
  }

  function normalize(str) {
    return str ? str.toLowerCase().replace(/\s+/g, '_') : null;
  }

  function generateEventId() {
    return Date.now() + '.' + Math.random().toString(36).slice(2, 12);
  }

  function parseCategories(value) {
    if (!value) return [];
    try {
      return typeof value === 'string' ? JSON.parse(value) : Array.isArray(value) ? value : [];
    } catch (_) { return []; }
  }

  function centsToFloat(cents) {
    return (cents || 0) / 100;
  }

  // ---------------------------------------------------------------------------
  // dataLayer helpers
  // ---------------------------------------------------------------------------

  function pushEvent(name, data) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: name };
    for (var k in data) { if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k]; }
    window.dataLayer.push(payload);
  }

  function pushEcommerce() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
  }

  // ---------------------------------------------------------------------------
  // Customer hashing
  // ---------------------------------------------------------------------------

  function getStr(v) {
    return typeof v === 'string' && v.trim() ? v.toLowerCase() : null;
  }

  async function hashCustomer(customer) {
    var raw = {
      id:           getStr(customer.id   ? String(customer.id) : null),
      email:        getStr(customer.email),
      phone_number: getStr(customer.phone),
      first_name:   getStr(customer.first_name),
      last_name:    getStr(customer.last_name),
      city:         getStr(customer.default_address && customer.default_address.city),
      zip_code:     getStr(customer.default_address && customer.default_address.zip),
      country:      getStr(customer.default_address && customer.default_address.country_code),
    };

    var result = {};
    var keys = Object.keys(raw);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (raw[key] !== null) {
        result[key] = await sha256(raw[key]);
      }
    }
    return result;
  }

  // ---------------------------------------------------------------------------
  // wSetup — fires on every page; handles customer hashing asynchronously
  // ---------------------------------------------------------------------------

  async function pushWSetup(overrides) {
    var pageData = Object.assign({}, window.goovi && window.goovi.pageData, overrides);
    if (!pageData.page_type) return;

    var eventData = {
      ss_event_id:       generateEventId(),
      country:           pageData.country,
      language:          pageData.language,
      environment:       'production',
      page_type:         pageData.page_type,
      cart_total_items:  pageData.cart_total_items || 0,
      cart_total_price:  pageData.cart_total_price || 0,
      currency:          pageData.currency || 'EUR',
    };

    if (pageData.first_level_category) {
      eventData.first_level_category = normalize(pageData.first_level_category) || undefined;
    }

    var customer = window.goovi && window.goovi.customer;
    if (customer) {
      try {
        var hash = await hashCustomer(customer);
        if (hash.id)           eventData.userID        = hash.id;
        if (hash.email)        eventData.email         = hash.email;
        if (hash.phone_number) eventData.phone_number  = hash.phone_number;
        var hashNoId = {};
        for (var k in hash) {
          if (k !== 'id' && Object.prototype.hasOwnProperty.call(hash, k)) {
            hashNoId[k] = hash[k];
          }
        }
        eventData.user_info = hashNoId;
      } catch (_) { /* hashing failed – push without customer data */ }
    }

    pushEvent('wSetup', eventData);
  }

  // ---------------------------------------------------------------------------
  // view_item — product detail page
  // ---------------------------------------------------------------------------

  function pushViewItem() {
    var data = window.goovi && window.goovi.productData;
    if (!data) return;

    var categories  = parseCategories(data.category_attributes);
    var price       = parseFloat(data.price) || 0;
    var cap         = parseFloat(data.compare_at_price) || 0;
    var discount    = cap > price ? parseFloat((cap - price).toFixed(2)) : 0;

    pushEcommerce();
    pushEvent('view_item', {
      ecommerce: {
        currency: (window.goovi.pageData && window.goovi.pageData.currency) || 'EUR',
        items: [{
          item_name:           normalize(data.title),
          item_id:             data.sku || '',
          price:               price,
          discount:            discount,
          item_category:       normalize(categories[0]),
          item_main_category:  normalize(categories[1]),
          quantity:            1,
          coupon:              'no_coupon',
        }],
      },
    });
  }

  // ---------------------------------------------------------------------------
  // view_item_list — collection page
  // ---------------------------------------------------------------------------

  function pushViewItemList() {
    var products = window.goovi && window.goovi.collectionProducts;
    if (!products || !products.length) return;

    var currency = (window.goovi.pageData && window.goovi.pageData.currency) || 'EUR';

    var items = products.map(function (product, index) {
      var categories = parseCategories(product.category_attributes);
      var price      = parseFloat(product.price) || 0;
      var cap        = parseFloat(product.compare_at_price) || 0;
      var discount   = cap > price ? parseFloat((cap - price).toFixed(2)) : 0;

      return {
        item_name:           normalize(product.title),
        item_id:             product.sku || '',
        price:               price,
        discount:            discount,
        item_category:       normalize(categories[0]),
        item_main_category:  normalize(categories[1]),
        index:               index,
        quantity:            1,
        coupon:              'no_coupon',
      };
    });

    pushEcommerce();
    pushEvent('view_item_list', {
      ecommerce: { currency: currency, items: items },
    });
  }

  // ---------------------------------------------------------------------------
  // view_cart — cart page
  // ---------------------------------------------------------------------------

  function pushViewCart(cartData) {
    var data = cartData || (window.goovi && window.goovi.cartData);
    if (!data) return;

    var currency = data.currency || (window.goovi.pageData && window.goovi.pageData.currency) || 'EUR';
    var value    = centsToFloat(data.total_price);

    var items = (data.items || []).map(function (item) {
      var categories = parseCategories(item.category_attributes);
      var price      = centsToFloat(item.price);
      var cap        = centsToFloat(item.compare_at_price);
      var discount   = cap > price ? parseFloat((cap - price).toFixed(2)) : 0;

      return {
        item_name:           normalize(item.product_title || item.title),
        item_id:             item.sku || '',
        price:               price,
        discount:            discount,
        item_category:       normalize(categories[0]),
        item_main_category:  normalize(categories[1]),
      };
    });

    pushEcommerce();
    pushEvent('view_cart', {
      ecommerce: { currency: currency, value: value, items: items },
    });
  }

  // ---------------------------------------------------------------------------
  // add_to_cart — called from jkl-product-form.js and jkl-cart.js
  // item: object from /cart/add.js response (price in cents)
  // cart: full cart state from /cart.js (price in cents), optional
  // ---------------------------------------------------------------------------

  function pushAddToCart(item, cart) {
    if (!item) return;

    // Try to get categories from the item, then fall back to current product data
    var catRaw     = item.category_attributes
      || (window.goovi && window.goovi.productData && window.goovi.productData.category_attributes)
      || null;
    var categories = parseCategories(catRaw);

    var price    = centsToFloat(item.price);
    var cap      = centsToFloat(item.compare_at_price);
    var discount = cap > price ? parseFloat((cap - price).toFixed(2)) : 0;
    var currency = (cart && cart.currency)
      || (window.goovi && window.goovi.pageData && window.goovi.pageData.currency)
      || 'EUR';

    pushEcommerce();
    pushEvent('add_to_cart', {
      ecommerce: {
        currency: currency,
        value:    cart ? centsToFloat(cart.total_price) : price,
        items: [{
          item_name:           normalize(item.title),
          item_id:             item.sku || '',
          price:               price,
          discount:            discount,
          item_category:       normalize(categories[0]),
          item_main_category:  normalize(categories[1]),
          quantity:            item.quantity || 1,
          coupon:              'no_coupon',
        }],
      },
    });

    // If currently on cart page, also refresh view_cart
    if (document.body.classList.contains('template-cart') && cart) {
      pushViewCart(cart);
    }
  }

  // ---------------------------------------------------------------------------
  // remove_from_cart — called from jkl-cart.js
  // item: object built from DOM data attributes (price in cents)
  // cart: full cart state from /cart.js (optional, fetched after removal)
  // ---------------------------------------------------------------------------

  function pushRemoveFromCart(item, cart) {
    if (!item) return;

    var categories = parseCategories(item.category_attributes);
    var price      = centsToFloat(item.price);
    var cap        = centsToFloat(item.compare_at_price);
    var discount   = cap > price ? parseFloat((cap - price).toFixed(2)) : 0;
    var currency   = (cart && cart.currency)
      || (window.goovi && window.goovi.pageData && window.goovi.pageData.currency)
      || 'EUR';

    pushEcommerce();
    pushEvent('remove_from_cart', {
      ecommerce: {
        currency: currency,
        value:    cart ? centsToFloat(cart.total_price) : 0,
        items: [{
          item_name:           normalize(item.title),
          item_id:             item.sku || '',
          price:               price,
          discount:            discount,
          item_category:       normalize(categories[0]),
          item_main_category:  normalize(categories[1]),
          quantity:            item.quantity || 1,
          coupon:              'no_coupon',
        }],
      },
    });

    // If currently on cart page, also refresh view_cart
    if (document.body.classList.contains('template-cart') && cart) {
      pushViewCart(cart);
    }
  }

  // ---------------------------------------------------------------------------
  // add_to_wishlist / remove_from_wishlist — called from jkl-wishlist.js
  // btn: the [data-wishlist-btn] button element
  // ---------------------------------------------------------------------------

  function pushAddToWishlist(btn) {
    if (!btn) return;
    var title      = btn.dataset.productTitle || '';
    var sku        = btn.dataset.productSku || '';
    var price      = parseFloat(btn.dataset.productPrice) || 0;
    var cap        = parseFloat(btn.dataset.compareAtPrice) || 0;
    var catRaw     = btn.dataset.categoryAttributes || null;
    var categories = parseCategories(catRaw);
    var discount   = cap > price ? parseFloat((cap - price).toFixed(2)) : 0;
    var currency   = (window.goovi && window.goovi.pageData && window.goovi.pageData.currency) || 'EUR';

    pushEcommerce();
    pushEvent('add_to_wishlist', {
      ecommerce: {
        currency: currency,
        value:    price,
        items: [{
          item_name:           normalize(title),
          item_id:             sku,
          price:               price,
          discount:            discount,
          item_category:       normalize(categories[0]),
          item_main_category:  normalize(categories[1]),
          index:               0,
          quantity:            1,
          coupon:              'no_coupon',
        }],
      },
    });
  }

  function pushRemoveFromWishlist(btn) {
    if (!btn) return;
    pushEcommerce();
    pushEvent('GA4event', {
      eventName: 'remove_from_wishlist',
      product:   normalize(btn.dataset.productTitle || ''),
      position:  null,
    });
  }

  // ---------------------------------------------------------------------------
  // search_bar
  // ---------------------------------------------------------------------------

  function pushSearchBar(searchTerm, type) {
    pushEvent('GA4event', {
      eventName:  'search_bar',
      searchTerm: searchTerm,
      type:       type,
    });
  }

  // ---------------------------------------------------------------------------
  // Interaction listeners — set up once on DOMContentLoaded
  // ---------------------------------------------------------------------------

  function setupInteractionListeners() {
    // ── select_item: product card click
    document.addEventListener('click', function (e) {
      var card = e.target.closest('[data-analytics-product-card]');
      if (!card) return;

      var categories = parseCategories(card.dataset.categoryAttributes || null);
      var price      = parseFloat(card.dataset.price) || 0;
      var cap        = parseFloat(card.dataset.compareAtPrice) || 0;
      var discount   = cap > price ? parseFloat((cap - price).toFixed(2)) : 0;
      var currency   = (window.goovi && window.goovi.pageData && window.goovi.pageData.currency) || 'EUR';

      pushEcommerce();
      pushEvent('select_item', {
        ecommerce: {
          currency: currency,
          items: [{
            item_name:           normalize(card.dataset.productTitle || ''),
            item_id:             card.dataset.sku || '',
            price:               price,
            discount:            discount,
            item_category:       normalize(categories[0]),
            item_main_category:  normalize(categories[1]),
            quantity:            1,
            coupon:              'no_coupon',
          }],
        },
      });
    });

    // ── mini_cart: cart icon / open-cart button
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-jkl-cart-open]')) {
        pushEvent('GA4event', { eventName: 'mini_cart', label: 'view_cart' });
      }
    });

    // ── social links
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-analytics-social]');
      if (!link) return;
      pushEvent('GA4event', {
        eventName: 'social',
        label:     normalize(link.dataset.analyticsLabel || link.getAttribute('aria-label') || ''),
        position:  link.dataset.analyticsPosition || 'footer',
      });
    });

    // ── footer links
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-analytics-footer]');
      if (!link) return;
      pushEvent('GA4event', {
        eventName: 'footer',
        label:     normalize(link.dataset.analyticsLabel || link.textContent.trim()),
        type:      normalize(link.dataset.analyticsType || 'link'),
      });
    });

    // ── header CTAs
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-analytics-header]');
      if (!link) return;
      pushEvent('GA4event', {
        eventName: 'header',
        position:  link.dataset.analyticsPosition || '',
        label:     link.dataset.analyticsLabel || link.textContent.trim(),
      });
    });

    // ── menu links (mega menu, mobile nav)
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-analytics-menu]');
      if (!link) return;
      pushEvent('GA4event', {
        eventName: 'menu',
        position:  link.dataset.analyticsPosition || '',
        label:     normalize(link.dataset.analyticsLabel || link.textContent.trim()),
      });
    });

    // ── collection filter clicks (navigate to subcollection)
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-analytics-filter]');
      if (!link) return;
      pushEvent('GA4event', {
        eventName: 'add_listing_filter',
        action:    link.dataset.analyticsAction || 'select',
        label:     link.dataset.analyticsLabel || normalize(link.textContent.trim()),
      });
    });

    // ── collection sort changes
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-analytics-sort]');
      if (!link) return;
      pushEvent('GA4event', {
        eventName: 'listing_sorting_by',
        label:     normalize(link.dataset.analyticsLabel || link.textContent.trim()),
      });
    });

    // ── search form submit (search_frequent)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches('form[action*="/search"]') && !form.classList.contains('store-search-form')) return;
      var input = form.querySelector('input[name="q"]');
      if (input && input.value.trim()) {
        pushSearchBar(input.value.trim(), 'search_frequent');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Page-level events — fire once after DOM is ready
  // ---------------------------------------------------------------------------

  async function onPageReady() {
    var goovi    = window.goovi || {};
    var pageData = goovi.pageData || {};
    var pageType = pageData.page_type || 'other';

    setupInteractionListeners();

    // ── product page: wSetup + view_item
    if (pageType === 'product') {
      await pushWSetup({ first_level_category: goovi.productData && goovi.productData.product_title });
      pushViewItem();
      return;
    }

    // ── collection page: wSetup + view_item_list
    if (pageType === 'collection') {
      await pushWSetup();
      pushViewItemList();
      return;
    }

    // ── cart page: wSetup + view_cart
    if (pageType === 'cart') {
      await pushWSetup();
      if (goovi.cartData) pushViewCart();
      return;
    }

    // ── search results page: wSetup + search_bar (search_listing)
    if (pageType === 'search') {
      await pushWSetup();
      var q = new URLSearchParams(window.location.search).get('q') || '';
      if (q) pushSearchBar(q, 'search_listing');
      return;
    }

    // ── all other pages (home, page, account, etc.): wSetup only
    await pushWSetup();
  }

  // Guard against DOMContentLoaded having already fired before this deferred
  // script executed — registering a listener after the fact would never trigger.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onPageReady);
  } else {
    onPageReady();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  window.gooviAnalytics = {
    pushEvent:              pushEvent,
    pushEcommerce:          pushEcommerce,
    pushWSetup:             pushWSetup,
    pushViewCart:           pushViewCart,
    pushAddToCart:          pushAddToCart,
    pushRemoveFromCart:     pushRemoveFromCart,
    pushAddToWishlist:      pushAddToWishlist,
    pushRemoveFromWishlist: pushRemoveFromWishlist,
    pushSearchBar:          pushSearchBar,
    normalize:              normalize,
    parseCategories:        parseCategories,
    sha256:                 sha256,
  };
})();
